import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import { MATCH_POLICY, compilePattern, emptyTerm, termError } from '../brand';

/**
 * The brand definition, written in the scope bar beside the dates it will be read over.
 *
 * It belongs here and not in a block of its own because it is part of the request: both sides of
 * this report are the same expression sent twice, so a term that has been typed but not applied
 * has changed nothing about the figures on screen. Behind the same Apply as the dates, the page
 * can only ever show one definition's results, and the block below states which.
 *
 * The compiled expression is shown as it is written, before anything is applied. It is the whole
 * of what Search Console will be told, and the difference between what someone typed and what
 * gets matched — an escaped dot, a term that turns out to be a substring of another — is visible
 * only in the compiled form.
 *
 * Nothing is saved. The definition lives in this page's address for as long as the tab is open
 * and nowhere else: there is no account-level brand setting behind this, and pretending otherwise
 * by remembering it would leave users with a rule they could not find to change.
 */
class BrandTerms extends Component {
    constructor(props) {
        super(props);
        this.state = { draft: emptyTerm(), error: null };

        this.handleAdd = this.handleAdd.bind(this);
    }

    patch(partial) {
        this.setState((state) => ({ draft: { ...state.draft, ...partial }, error: null }));
    }

    handleAdd(event) {
        event.preventDefault();

        const { t, terms, onChange } = this.props;
        const draft = {
            name: this.state.draft.name.trim(),
            match: this.state.draft.match.trim(),
        };

        const error = termError(draft, terms, t);
        if (error) return this.setState({ error });

        onChange([...terms, draft]);
        this.setState({ draft: emptyTerm(), error: null });
    }

    renderPreview() {
        const { t, policy, terms, pattern } = this.props;
        const compiled = compilePattern({ policy, terms, pattern });

        return (
            <div className="brand-pattern-preview">
                <h5>{t('tools.brand_non_brand.terms.compiled')}</h5>
                {compiled ? (
                    <code className="gsc-tool-expression">{compiled}</code>
                ) : (
                    <span className="gsc-tool-field-note">
                        {t('tools.brand_non_brand.terms.compiled_empty')}
                    </span>
                )}
                <p className="gsc-tool-rules-note">
                    {t('tools.brand_non_brand.terms.compiled_note')}
                </p>
            </div>
        );
    }

    renderEditor() {
        const { t, terms, onChange } = this.props;
        const { draft, error } = this.state;

        return (
            <>
                <div className="gsc-tool-rules-grid brand-terms-grid">
                    <div className="gsc-tool-field">
                        <label htmlFor="bnb-term-match">
                            {t('tools.brand_non_brand.terms.match')}
                        </label>
                        <input
                            id="bnb-term-match"
                            type="text"
                            className="form-control"
                            value={draft.match}
                            placeholder={t('tools.brand_non_brand.terms.match_placeholder')}
                            onChange={(event) => this.patch({ match: event.target.value })}
                        />
                    </div>

                    <div className="gsc-tool-field">
                        <label htmlFor="bnb-term-name">
                            {t('tools.brand_non_brand.terms.name')}
                        </label>
                        <input
                            id="bnb-term-name"
                            type="text"
                            className="form-control"
                            value={draft.name}
                            placeholder={t('tools.brand_non_brand.terms.name_placeholder')}
                            onChange={(event) => this.patch({ name: event.target.value })}
                        />
                    </div>

                    <div className="gsc-tool-rules-add">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleAdd}
                        >
                            {t('tools.brand_non_brand.terms.add')}
                        </button>
                    </div>
                </div>

                <p className="gsc-tool-rules-note">
                    {t('tools.brand_non_brand.terms.escape_note')}
                </p>

                {!!error && <div className="gsc-tool-banner error">{error}</div>}

                {!terms.length ? (
                    <p className="gsc-tool-field-note">{t('tools.brand_non_brand.terms.none')}</p>
                ) : (
                    <div className="gsc-tool-chips">
                        {terms.map((term, index) => (
                            <span className="gsc-tool-chip" key={`${term.match}-${index}`}>
                                <span>
                                    {term.name ? `${term.name}: ${term.match}` : term.match}
                                </span>
                                <button
                                    type="button"
                                    aria-label={t('tools.brand_non_brand.terms.remove', {
                                        term: term.match,
                                    })}
                                    onClick={() => onChange(terms.filter((_, at) => at !== index))}
                                >
                                    <i className="icon-x-close" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </>
        );
    }

    render() {
        const { t, policy } = this.props;

        return (
            <div className="gsc-tool-rules">
                <div className="gsc-tool-rules-head">
                    <h4>{t('tools.brand_non_brand.terms.title')}</h4>
                    <p>{t('tools.brand_non_brand.terms.description')}</p>
                </div>

                {/* An advanced rule is a single expression, typed in the control grid above;
                    there are no alternatives to collect, so the editor is not in the way. */}
                {policy === MATCH_POLICY.LITERAL && this.renderEditor()}

                {this.renderPreview()}
            </div>
        );
    }
}

export default withTranslation()(BrandTerms);
